import { useRef } from 'react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import { useFinancialWorkspace } from '../../FinancialWorkspace/Providers/Provider'
import {
  InsightTableGroupBy,
  InsightTablePeriod,
  PaginatedTableItem,
  TableDataVariables,
} from '../../FinancialWorkspace/types/table'
import { Agreements } from '../components/Table/components/CustomChildren/Agreements'
import { Expenses } from '../components/Table/components/CustomChildren/Expenses'
import { LazyExpenses } from '../components/Table/components/CustomChildren/LazyExpenses'
import { Payroll } from '../components/Table/components/CustomChildren/Payroll'
import { mergeDataAndComparisonData } from '../lib/dataToRow'
import { getFinanceTypes } from '../lib/sources'
import { useFinanceSummaries } from './useFinanceSummaries'
import { useSourcesVisibility } from './useSourcesVisibility'

interface ReturnedData {
  loading: boolean
  refetch: () => void
  pagination?: {
    endCursor?: string
    totalCount?: number
    hasNextPage?: boolean
  }
  data: PaginatedTableItem[]
}

enum FinanceTypes {
  Payroll = 'payroll',
  Expense = 'expense',
  Agreement = 'agreement',
  Compensations = 'compensations',
}

export const useGroupedEmployeeCostData = (variables: TableDataVariables): ReturnedData => {
  const memoItems = useRef<PaginatedTableItem[]>([])

  const totalCount = useRef<number>(0)
  const groupBy = variables?.groupBy
  const resetPagination = variables?.resetPagination

  const visibility = useSourcesVisibility()
  const financeTypes = getFinanceTypes(visibility)
  const { hasComparisonDate } = useFinancialWorkspace()

  const onlyOneFinanceTypeVisible = financeTypes.length === 1

  const checkIsolatedType = useUnsafeCallback(
    (type: FinanceTypes) => onlyOneFinanceTypeVisible && financeTypes[0] == type,
    [financeTypes, onlyOneFinanceTypeVisible]
  )

  const { data, loading, fetchFinanceSummaries, refetch, pagination, filterVariables } =
    useFinanceSummaries({
      financeTypes,
      groupBy: variables?.groupBy,
      variables: {
        search: variables?.search,
        after: variables?.after ?? undefined,
        itemsPerPage: variables?.itemsPerPage,
      },
    })

  const {
    data: comparisonData,
    loading: comparisonLoading,
    refetch: comparisonRefetch,
    fetchFinanceSummaries: fetchComparisonFinanceSummaries,
  } = useFinanceSummaries({
    financeTypes,
    compareTo: true,
    groupBy: variables?.groupBy,
    variables: {
      search: variables?.search,
      after: variables?.after ?? undefined,
      itemsPerPage: variables?.itemsPerPage,
    },
  })

  const customChildren = useUnsafeMemo(() => {
    if (checkIsolatedType(FinanceTypes.Expense)) return [{ component: LazyExpenses }]

    if (
      !checkIsolatedType(FinanceTypes.Agreement) &&
      !checkIsolatedType(FinanceTypes.Payroll) &&
      !checkIsolatedType(FinanceTypes.Compensations)
    ) {
      return [{ component: Payroll }, { component: Expenses }, { component: Agreements }]
    }
  }, [checkIsolatedType])

  const formattedData = useUnsafeCallback(() => {
    const { data: newData, comparisonData: newComparisonData } = mergeDataAndComparisonData(
      data,
      comparisonData
    )

    return (
      newData.map((item, index) => ({
        id: item.id,
        customChildren,
        label: item.label,
        data: item.rows.map((row) => ({
          label: row.columnLabel,
          range: row.columnLabel,
          period: InsightTablePeriod.Monthly,
          totalAmountInCents: row.amountInCents,
        })),
        comparisonData: newComparisonData[index]?.rows?.map((row) => ({
          label: row.columnLabel,
          range: row.columnLabel,
          period: InsightTablePeriod.Monthly,
          totalAmountInCents: row.amountInCents,
        })),
        children: (item?.children || []).map((child) => ({
          id: child.label,
          label: child.label,
          data: child.rows?.map((row) => ({
            label: row.columnLabel,
            range: row.columnLabel,
            period: InsightTablePeriod.Monthly,
            totalAmountInCents: row.amountInCents,
          })),
          grey: checkIsolatedType(FinanceTypes.Agreement),
          dashed: checkIsolatedType(FinanceTypes.Agreement),
          italic: checkIsolatedType(FinanceTypes.Agreement),
        })),
        expanded: false,
      })) ?? []
    )
  }, [data, customChildren, comparisonData, checkIsolatedType])

  const filterVariablesString = JSON.stringify(filterVariables)

  useUnsafeEffect(() => {
    memoItems.current = []
    resetPagination?.()
  }, [filterVariablesString])

  useUnsafeEffect(() => {
    if (groupBy && groupBy !== InsightTableGroupBy.Total) {
      fetchFinanceSummaries()
      if (hasComparisonDate) fetchComparisonFinanceSummaries()
    }
  }, [fetchFinanceSummaries, groupBy, fetchComparisonFinanceSummaries, hasComparisonDate])

  const newItems = useUnsafeMemo(() => {
    if (data && (!hasComparisonDate || comparisonData)) {
      const newUniqueItems = formattedData().filter(
        (e) => !memoItems.current.find((i) => i.id === e.id)
      )
      memoItems.current = newUniqueItems.length
        ? [...memoItems.current, ...newUniqueItems]
        : memoItems.current

      totalCount.current = data.length

      return memoItems.current.filter((i) => i.id !== 'loading')
    } else {
      return memoItems.current.filter((i) => i.id !== 'loading')
    }
  }, [data, formattedData, comparisonData, hasComparisonDate])

  const onRefetch = useUnsafeCallback(() => {
    refetch()
    if (hasComparisonDate) comparisonRefetch()
  }, [refetch, comparisonRefetch, hasComparisonDate])

  return {
    pagination,
    data: newItems,
    refetch: onRefetch,
    loading: loading || (hasComparisonDate && comparisonLoading),
  }
}
